import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Radio,
  RadioGroup,
  SimpleGrid,
  Text,
  UnorderedList,
  ListItem,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ChronoUnit, LocalDate } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";

import { Messages } from "../../../../core/api";
import SingleDatePicker from "../../../../shared/components/DatePicker/SingleDatePicker";
import Select from "../../../../shared/components/Select";
import useApi from "../../../../shared/hooks/useApi";
import { PatientId } from "../../../../shared/schema/schema";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../../../shared/utils/format-response-error";
import { BulkScheduleCertificationPeriod } from "../bulk-schedule-tasks.types";

type CertificationPeriodType = Messages["CertificationPeriodType"];

type Props = {
  isOpen: boolean;
  patientId: PatientId;
  patientName: string;
  existingPeriods: BulkScheduleCertificationPeriod[];
  onClose: () => void;
  onCreated: (period: BulkScheduleCertificationPeriod) => void;
};

const TYPE_OPTIONS: { value: CertificationPeriodType; label: string }[] = [
  { value: "START_OF_CARE", label: "Start of Care" },
  { value: "REASSESSMENT", label: "Re-assessment" },
];

/** Same presets as the AngularJS certification period modal. */
const DURATION_PRESET_DAYS = [30, 60, 90, 120, 180];
const DEFAULT_DURATION_DAYS = 180;
const CUSTOM_DURATION = "custom";

/**
 * The new period defaults to starting the day after the patient's latest
 * period ends, mirroring the AngularJS modal.
 */
function getDefaultDates(existingPeriods: BulkScheduleCertificationPeriod[]): {
  startDate: LocalDate | null;
  endDate: LocalDate | null;
} {
  const latest = [...existingPeriods].sort((a, b) => b.endDate.compareTo(a.endDate))[0];

  if (latest === undefined) {
    return { startDate: null, endDate: null };
  }

  const startDate = latest.endDate.plusDays(1);

  return { startDate, endDate: startDate.plusDays(DEFAULT_DURATION_DAYS) };
}

function formatRange(period: { startDate: LocalDate; endDate: LocalDate }) {
  return `${dateFormatter.toDate(period.startDate)} - ${dateFormatter.toDate(period.endDate)}`;
}

/**
 * Creates a certification period for one patient from inside the bulk
 * schedule modal, so the coordinator can bind the CMS485 task to a period that
 * does not exist yet. A React counterpart of `certificationPeriodModal.js`
 * (create mode only).
 */
export default function CreateCertificationPeriodModal(props: Props) {
  const { queries, mutations } = useApi();
  const queryClient = useQueryClient();
  const toast = useToast();

  const defaults = React.useMemo(
    () => getDefaultDates(props.existingPeriods),
    [props.existingPeriods]
  );

  const [type, setType] = React.useState<CertificationPeriodType>("REASSESSMENT");
  const [startDate, setStartDate] = React.useState<LocalDate | null>(defaults.startDate);
  const [endDate, setEndDate] = React.useState<LocalDate | null>(defaults.endDate);

  const durationDays =
    startDate !== null && endDate !== null ? startDate.until(endDate, ChronoUnit.DAYS) : null;

  const validationError = (() => {
    if (startDate === null || endDate === null) {
      return null;
    }

    if (durationDays !== null && durationDays < 1) {
      return "Duration between start date and end date should be at least 1 day.";
    }

    return null;
  })();

  const canCheckAnomalies = startDate !== null && endDate !== null && validationError === null;

  const anomaliesQuery = useQuery({
    ...queries.rnSchedulingMap.certificationPeriodAnomalies(props.patientId, {
      patientId: props.patientId,
      startDate: startDate ?? LocalDate.now(),
      endDate: endDate ?? LocalDate.now(),
      certificationPeriodId: null,
    }),
    enabled: props.isOpen && canCheckAnomalies,
  });

  const createMutation = useMutation({
    ...mutations.rnSchedulingMap.createCertificationPeriod(props.patientId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: queries.rnSchedulingMap.patientCertificationPeriods(props.patientId).queryKey,
      });
      toast({
        title: "Certification period created",
        description: formatRange(response.certificationPeriod),
        status: "success",
        position: "top",
      });
      props.onCreated(response.certificationPeriod);
      props.onClose();
    },
    onError: (error) => {
      toast({
        title: "Failed to create certification period",
        description: formatErrorResponse(error),
        status: "error",
        position: "top",
        isClosable: true,
      });
    },
  });

  function handleDurationChange(value: string) {
    if (value === CUSTOM_DURATION || startDate === null) {
      return;
    }

    setEndDate(startDate.plusDays(Number(value)));
  }

  function handleStartDateChange(date: LocalDate | null) {
    setStartDate(date);

    // Keep the chosen duration when the start date moves.
    if (date !== null && durationDays !== null && durationDays >= 1) {
      setEndDate(date.plusDays(durationDays));
    }
  }

  function handleCreate() {
    if (startDate === null || endDate === null || validationError !== null) {
      return;
    }

    createMutation.mutate({ type, startDate, endDate });
  }

  const selectedDurationPreset =
    durationDays !== null && DURATION_PRESET_DAYS.includes(durationDays)
      ? String(durationDays)
      : CUSTOM_DURATION;

  const isFormValid = startDate !== null && endDate !== null && validationError === null;

  return (
    <Modal closeOnOverlayClick={false} isOpen={props.isOpen} size="lg" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create certification period for {props.patientName}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Type</FormLabel>
              <Select
                allowUnselect={false}
                label="Type"
                multiple={false}
                options={TYPE_OPTIONS}
                value={type}
                onChange={(value) => {
                  if (value !== undefined) {
                    setType(value);
                  }
                }}
              />
              {type === "START_OF_CARE" && (
                <FormHelperText>
                  A start of care period also updates the patient&apos;s start of care date when it
                  is later than the current one.
                </FormHelperText>
              )}
            </FormControl>

            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired>
                <FormLabel>Start date</FormLabel>
                <SingleDatePicker
                  placeholderText="Start date"
                  selected={startDate}
                  onChange={handleStartDateChange}
                />
              </FormControl>

              <FormControl isRequired isInvalid={validationError !== null}>
                <FormLabel>End date</FormLabel>
                <SingleDatePicker
                  disabled={startDate === null}
                  placeholderText="End date"
                  selected={endDate}
                  onChange={setEndDate}
                />
                <FormErrorMessage>{validationError}</FormErrorMessage>
              </FormControl>
            </SimpleGrid>

            {startDate !== null && (
              <FormControl>
                <FormLabel>Duration</FormLabel>
                <RadioGroup value={selectedDurationPreset} onChange={handleDurationChange}>
                  <HStack flexWrap="wrap" spacing={4}>
                    <Radio value={CUSTOM_DURATION}>Custom</Radio>
                    {DURATION_PRESET_DAYS.map((days) => (
                      <Radio key={days} value={String(days)}>
                        {days} days
                      </Radio>
                    ))}
                  </HStack>
                </RadioGroup>
              </FormControl>
            )}

            {canCheckAnomalies && (
              <>
                {anomaliesQuery.isFetching && <Progress isIndeterminate size="xs" />}

                {anomaliesQuery.data !== undefined &&
                  anomaliesQuery.data.overlaps.length === 0 &&
                  anomaliesQuery.data.gapped === null && (
                    <Alert borderRadius="md" fontSize="sm" status="success">
                      <AlertIcon />
                      This period does not overlap with any period and is not gapped from the
                      previous one.
                    </Alert>
                  )}

                {anomaliesQuery.data !== undefined && anomaliesQuery.data.overlaps.length > 0 && (
                  <Alert alignItems="flex-start" borderRadius="md" fontSize="sm" status="warning">
                    <AlertIcon />
                    <VStack align="stretch" spacing={1}>
                      <Text>We have found one or more overlapping certification periods:</Text>
                      <UnorderedList>
                        {anomaliesQuery.data.overlaps.map((period) => (
                          <ListItem key={period.id}>{formatRange(period)}</ListItem>
                        ))}
                      </UnorderedList>
                    </VStack>
                  </Alert>
                )}

                {anomaliesQuery.data?.gapped != null && (
                  <Alert borderRadius="md" fontSize="sm" status="warning">
                    <AlertIcon />
                    This certification period has a gap of more than one day from the previous
                    period ({formatRange(anomaliesQuery.data.gapped)}).
                  </Alert>
                )}

                {anomaliesQuery.isError && (
                  <Alert borderRadius="md" fontSize="sm" status="error">
                    <AlertIcon />
                    We have failed to check certification period anomalies.
                  </Alert>
                )}
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            isDisabled={createMutation.isPending}
            mr={3}
            variant="ghost"
            onClick={props.onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isDisabled={!isFormValid}
            isLoading={createMutation.isPending}
            onClick={handleCreate}
          >
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
